import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { ChevronRight, Home } from 'lucide-react'
import { classNames } from '@/ui/utils/main'

// ── Full-bleed context ────────────────────────────────────────────────────────
// PublicLayout sets this to true so every Breadcrumb inside automatically
// breaks out of its constrained container and spans the full viewport width.

interface BreadcrumbContextValue {
  fullBleed: boolean
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({ fullBleed: false })

export function BreadcrumbProvider({
  children,
  fullBleed = false,
}: {
  children: ReactNode
  fullBleed?: boolean
}) {
  return <BreadcrumbContext.Provider value={{ fullBleed }}>{children}</BreadcrumbContext.Provider>
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  /** Texte affiché */
  label: string
  /** URL cible. Si absent, l'item est la page courante (non cliquable). */
  href?: string
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
  className?: string
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  const { fullBleed } = useContext(BreadcrumbContext)
  if (!items.length) return null

  return (
    <nav
      aria-label="Fil d'Ariane"
      className={classNames(
        fullBleed ? 'w-screen ml-[calc(50%-50vw)] px-[10px]' : 'w-full',
        'flex items-center flex-wrap gap-0.5 text-xs text-muted-foreground mb-5',
        className,
      )}
    >
      {items.map((item, index) => (
        <span key={index} className="flex items-center gap-0.5">
          {index > 0 && <ChevronRight className="w-3 h-3 shrink-0 opacity-40 mx-0.5" />}
          {item.href ? (
            <a
              href={item.href}
              className="flex items-center gap-1 hover:text-foreground transition-colors no-underline hover:no-underline"
            >
              {index === 0 && <Home className="w-3 h-3 shrink-0" />}
              <span>{item.label}</span>
            </a>
          ) : (
            <span className="text-foreground/70 font-medium truncate max-w-[180px] sm:max-w-[260px]">
              {item.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  )
}
